import { useForm, type FieldValues, type UseFormProps } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

export { z };

// useZodForm wires react-hook-form to a zod schema with on-blur validation
// (so fields validate as the user moves through the form, not only on
// submit). Returns the standard react-hook-form API.
export function useZodForm<S extends z.ZodType<FieldValues>>(
  schema: S,
  options?: Omit<UseFormProps<z.infer<S>>, "resolver">,
) {
  return useForm<z.infer<S>>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(schema as any) as any,
    mode: "onBlur",
    ...options,
  });
}

// Shared field schemas. Email/URL use regexes (not zod's version-specific
// .email()/.url()) so they're stable across zod releases.
export const emailSchema = z
  .string()
  .trim()
  .min(1, "Email is required")
  .regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/, "Enter a valid email address");

export const serverNameSchema = z
  .string()
  .trim()
  .min(1, "Name is required")
  .max(64, "Keep it under 64 characters")
  .regex(/^[A-Za-z0-9_-]+$/, "Use letters, numbers, hyphens, or underscores only");

export const httpUrlSchema = z
  .string()
  .trim()
  .min(1, "URL is required")
  .regex(/^https?:\/\/.+/i, "Enter a URL starting with http:// or https://");

export const nonEmpty = (label: string) => z.string().trim().min(1, `${label} is required`);
